import { useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { formatUsd } from '#/shared/lib/format'
import { useTableSearch } from '#/shared/lib/table'
import { Button } from '#/shared/ui/button'
import { DataDropdown } from '#/shared/ui/data-dropdown'
import type { SummaryItem } from '#/shared/ui/data-dropdown'
import { FiltersButton } from '#/shared/ui/filters-button'
import {
  ArchiveIcon,
  EmailIcon,
  PhoneIcon,
  SmsIcon,
  UserCardIcon,
} from '#/shared/ui/icons'
import { MassActions } from '#/shared/ui/mass-actions'
import { SearchInput } from '#/shared/ui/search-input'
import { Toolbar, ToolbarSwap } from '#/shared/ui/toolbar'
import { Tooltip } from '#/shared/ui/tooltip'
import { UserDropdown, USER_DROPDOWN_VARIANTS } from '#/shared/ui/user-dropdown'
import type { UserDropdownVariant, UserOption } from '#/shared/ui/user-dropdown'
import { OWNER_ALL_USERS } from '../lib/owner-filter'
import { useOwnerUser } from '../lib/use-owner-user'
import type { OwnerUserState } from '../lib/use-owner-user'
import { useToolbarUsers } from '../lib/use-toolbar-users'
import type { ShipmentStage } from '../model/stages'

function ToolbarSearch({
  placeholder,
  disabled,
}: {
  placeholder: string
  disabled?: boolean
}) {
  const { value, onChange } = useTableSearch()
  return (
    <SearchInput
      size="sm"
      clearable
      className="max-sm:flex-1"
      placeholder={placeholder}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
    />
  )
}

function parseSum(raw: number | undefined): number | null {
  if (raw == null) return null
  return Number.isFinite(raw) ? raw : null
}

function buildSummaryItems(
  t: TFunction,
  total: number,
  totalTariff: number | undefined,
  totalDeposit: number | undefined,
): SummaryItem[] {
  return [
    { label: t('toolbar.pagination.all'), value: total.toLocaleString() },
    {
      label: t('toolbar.pagination.sumTariff'),
      value: formatUsd(parseSum(totalTariff), { showCents: true }),
    },
    {
      label: t('toolbar.pagination.sumDeposit'),
      value: formatUsd(parseSum(totalDeposit), { showCents: true }),
    },
  ]
}

function resolveFilters(
  filters: ShipmentListToolbarProps['filters'],
  disabled: boolean,
): ReactNode {
  if (typeof filters === 'function') return filters({ disabled })
  return filters ?? <FiltersButton disabled={disabled} />
}

function OwnerDropdown({
  owner,
  me,
  users,
  ownerUserId,
  allCount,
  showCounts,
  variant,
  onSearchChange,
}: {
  owner: OwnerUserState
  me: UserOption | undefined
  users: UserOption[]
  ownerUserId: string | null | undefined
  allCount: number | undefined
  showCounts: boolean
  variant: UserDropdownVariant | undefined
  onSearchChange: (value: string) => void
}) {
  const isTeams = variant === USER_DROPDOWN_VARIANTS.TEAMS
  if (!isTeams && !owner.currentUser) return null
  // The generic dropdown treats `null` as "All Users"; map our explicit
  // `all` sentinel back to null so the trigger and highlighted row are right.
  const selectedUserId = ownerUserId === OWNER_ALL_USERS ? null : ownerUserId
  return (
    <UserDropdown
      variant={variant}
      currentUser={owner.currentUser}
      meUser={me}
      users={users}
      selectedUserId={selectedUserId}
      allUsersCount={allCount}
      showCounts={showCounts}
      align="right"
      onSearchChange={onSearchChange}
      onSelect={owner.onSelect}
      onClear={owner.onClear}
    />
  )
}

// Wraps a not-yet-built mass action in a disabled button + "coming soon" tooltip.
// The button must be wrapped in a span — a disabled button swallows the hover
// events the tooltip trigger relies on, so the span carries them instead.
function ComingSoonAction({
  icon,
  label,
  t,
}: {
  icon: ReactNode
  label: string
  t: TFunction
}) {
  return (
    <Tooltip content={t('toolbar.actions.comingSoon')}>
      <span className="inline-flex">
        <MassActions.Button icon={icon} label={label} disabled />
      </span>
    </Tooltip>
  )
}

function AutoDialAction({
  onAutoDial,
  disabledReason,
  t,
}: {
  onAutoDial?: () => void
  disabledReason?: string | null
  t: TFunction
}) {
  const button = (
    <MassActions.Button
      icon={<PhoneIcon className="size-4.5 text-primary" />}
      label={t('toolbar.actions.autoDial')}
      onClick={onAutoDial}
      disabled={disabledReason != null}
    />
  )
  if (disabledReason == null) return button
  return (
    <Tooltip content={disabledReason}>
      <span className="inline-flex">{button}</span>
    </Tooltip>
  )
}

function ShipmentMassActions({
  count,
  onSms,
  onEmail,
  onAutoDial,
  autoDialDisabledReason,
}: {
  count: number
  onSms?: () => void
  onEmail?: () => void
  onAutoDial?: () => void
  autoDialDisabledReason?: string | null
}) {
  const { t } = useTranslation()
  return (
    <MassActions count={count}>
      <div className="flex items-center gap-2.5">
        <MassActions.Button
          icon={<SmsIcon className="size-4.5 text-primary" />}
          label={t('toolbar.actions.sms')}
          onClick={onSms}
        />
        <MassActions.Button
          icon={<EmailIcon className="size-4.5 text-primary" />}
          label={t('toolbar.actions.email')}
          onClick={onEmail}
        />
        <ComingSoonAction
          icon={<UserCardIcon className="size-4 text-primary" />}
          label={t('toolbar.actions.reassign')}
          t={t}
        />
        <AutoDialAction
          onAutoDial={onAutoDial}
          disabledReason={autoDialDisabledReason}
          t={t}
        />
      </div>
      <MassActions.Divider />
      <ComingSoonAction
        icon={<ArchiveIcon className="size-4 text-destructive" />}
        label={t('toolbar.actions.archive')}
        t={t}
      />
    </MassActions>
  )
}

interface ToolbarRightSectionProps {
  rangeFrom: number
  rangeTo: number
  total: number
  totalTariff?: number
  totalDeposit?: number
  onRangeChange: (from: number, to: number) => void
  rangeDisabled?: boolean
  owner: OwnerUserState
  me: UserOption | undefined
  users: UserOption[]
  ownerUserId?: string | null
  allCount: number | undefined
  showCounts: boolean
  userDropdownVariant?: UserDropdownVariant
  onOwnerSearchChange: (value: string) => void
  newButtonLabel: string
  onNewClick?: () => void
}

function ToolbarRightSection({
  rangeFrom,
  rangeTo,
  total,
  totalTariff,
  totalDeposit,
  onRangeChange,
  rangeDisabled,
  owner,
  me,
  users,
  ownerUserId,
  allCount,
  showCounts,
  userDropdownVariant,
  onOwnerSearchChange,
  newButtonLabel,
  onNewClick,
}: ToolbarRightSectionProps) {
  const { t } = useTranslation()
  return (
    <Toolbar.Right className="max-sm:flex-wrap">
      <DataDropdown
        from={rangeFrom}
        to={rangeTo}
        total={total}
        onPageChange={onRangeChange}
        disabled={rangeDisabled}
        summaryItems={buildSummaryItems(t, total, totalTariff, totalDeposit)}
      />
      <OwnerDropdown
        owner={owner}
        me={me}
        users={users}
        ownerUserId={ownerUserId}
        allCount={allCount}
        showCounts={showCounts}
        variant={userDropdownVariant}
        onSearchChange={onOwnerSearchChange}
      />
      {onNewClick && (
        <Button variant="warning" size="xs" pressEffect onClick={onNewClick}>
          {newButtonLabel}
        </Button>
      )}
    </Toolbar.Right>
  )
}

export interface ShipmentListToolbarProps {
  stage?: ShipmentStage
  /**
   * Active status-tab filter. Narrows the teams-dropdown counts to that status
   * (matching the list), so each user's number reflects the selected tab.
   */
  status?: string | null
  selectedCount?: number
  searchPlaceholder: string
  newButtonLabel: string
  onNewClick?: () => void
  filters?: ReactNode | ((ctx: { disabled: boolean }) => ReactNode)
  /**
   * Inline filters panel, rendered between the toolbar bar and the chip row —
   * it slides down in flow and pushes the table down while open. The panel
   * decides its own visibility (returns null when closed).
   */
  filtersPanel?: ReactNode
  rangeFrom: number
  rangeTo: number
  total: number
  totalTariff?: number
  totalDeposit?: number
  onRangeChange: (from: number, to: number) => void
  rangeDisabled?: boolean
  isFiltered?: boolean
  activeFilters?: ReactNode
  ownerUserId?: string | null
  onOwnerUserChange?: (userId: string | null) => void
  userDropdownVariant?: UserDropdownVariant
  onMassSms?: () => void
  onMassEmail?: () => void
  onAutoDial?: () => void
  /** When set, the Power Dial mass action renders disabled with this reason
   *  as its tooltip. */
  autoDialDisabledReason?: string | null
}

export function ShipmentListToolbar({
  stage,
  status,
  selectedCount = 0,
  searchPlaceholder,
  newButtonLabel,
  onNewClick,
  filters,
  filtersPanel,
  rangeFrom,
  rangeTo,
  total,
  totalTariff,
  totalDeposit,
  onRangeChange,
  rangeDisabled,
  isFiltered = false,
  activeFilters,
  ownerUserId,
  onOwnerUserChange,
  userDropdownVariant,
  onMassSms,
  onMassEmail,
  onAutoDial,
  autoDialDisabledReason,
}: ShipmentListToolbarProps) {
  const [userSearch, setUserSearch] = useState('')
  const { users, me, allCount, countsAvailable } = useToolbarUsers(
    stage,
    userSearch,
    status,
  )
  const owner = useOwnerUser(
    users,
    me,
    ownerUserId,
    onOwnerUserChange,
    userDropdownVariant,
  )
  const hasSelection = selectedCount > 0
  const inputsDisabled = total === 0 && !isFiltered
  const filtersNode = resolveFilters(filters, inputsDisabled)

  return (
    <Toolbar>
      <Toolbar.Bar className="max-sm:flex-col max-sm:items-stretch max-sm:gap-2.5">
        <Toolbar.Left>
          <ToolbarSwap
            className="max-sm:w-full"
            showAlt={hasSelection}
            primary={
              <>
                <ToolbarSearch
                  placeholder={searchPlaceholder}
                  disabled={inputsDisabled}
                />
                {filtersNode}
              </>
            }
            alt={
              <ShipmentMassActions
                count={selectedCount}
                onSms={onMassSms}
                onEmail={onMassEmail}
                onAutoDial={onAutoDial}
                autoDialDisabledReason={autoDialDisabledReason}
              />
            }
          />
        </Toolbar.Left>
        <ToolbarRightSection
          rangeFrom={rangeFrom}
          rangeTo={rangeTo}
          total={total}
          totalTariff={totalTariff}
          totalDeposit={totalDeposit}
          onRangeChange={onRangeChange}
          rangeDisabled={rangeDisabled}
          owner={owner}
          me={me}
          users={users}
          ownerUserId={ownerUserId}
          allCount={allCount}
          showCounts={countsAvailable}
          userDropdownVariant={userDropdownVariant}
          onOwnerSearchChange={setUserSearch}
          newButtonLabel={newButtonLabel}
          onNewClick={onNewClick}
        />
      </Toolbar.Bar>
      {filtersPanel}
      {activeFilters ? (
        <Toolbar.Filters>{activeFilters}</Toolbar.Filters>
      ) : null}
    </Toolbar>
  )
}
